<template>
    <div style="background: grey">
        <div style="margin: 0 0 10px">
            <slot name="actions" v-bind="props">
                <n-button size="tiny" color="black" tag="a" :href="props.doc.link" target="_blank">download</n-button>&nbsp;
                <n-button size="tiny" color="black" tag="a" target="_blank" :href="props.doc.path">open</n-button>
            </slot>
        </div>
        <div v-if="officeViewerUrl" class="office-outer-box" :style="heightStyle">
            <iframe :src="officeViewerUrl" width="100%" :height="heightStyle['max-height']" style="border: none" frameborder="0"></iframe>
        </div>
        <div v-else-if="isLoading" class="flex items-center justify-center p-8">
            <div class="text-center">
                <i class="fas fa-spinner fa-spin mb-4 text-2xl text-gray-400"></i>
                <p class="text-sm text-gray-500">Loading preview...</p>
            </div>
        </div>
        <div v-else class="flex items-center justify-center p-8">
            <div class="text-center">
                <i class="fas fa-exclamation-circle mb-4 text-2xl text-red-500"></i>
                <p class="text-sm text-red-500">Failed to load preview</p>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useViewPortHeight } from '@/hooks';
import type { IDocument } from '@condo/domain';
import { getDriveId } from '@condo/domain';
import { getSignedDownloadUrl } from '@/api/basecamp.api';
import { NButton } from 'naive-ui';
import { computed, onMounted, ref } from 'vue';

interface Props {
    doc: IDocument;
    maxHeight?: string;
}
const props = defineProps<Props>();
const viewportHeight = useViewPortHeight();

const isLoading = ref(true);
const officeViewerUrl = ref<string>('');

const heightStyle = computed(() => {
    return { 'max-height': props.maxHeight ?? viewportHeight.value };
});

onMounted(async () => {
    try {
        const { uuid, filename, path, link } = props.doc;
        const isGoogleDrive = path?.includes('drive.google.com') || link?.includes('drive.google.com');
        const isGcpBucketDocument = uuid && !isGoogleDrive;

        if (isGoogleDrive) {
            const driveId = getDriveId({ link, path } as IDocument);
            if (driveId) {
                const driveDownloadUrl = `https://drive.google.com/uc?export=download&id=${driveId}`;
                officeViewerUrl.value = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(driveDownloadUrl)}`;
            } else {
                officeViewerUrl.value = path || link || '';
            }
        } else if (isGcpBucketDocument) {
            const { url } = await getSignedDownloadUrl(uuid, filename || 'document');
            officeViewerUrl.value = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
        } else {
            officeViewerUrl.value = path || link || '';
        }
    } catch (error) {
        console.error('Failed to load office document preview:', error);
    } finally {
        isLoading.value = false;
    }
});
</script>

<style scoped>
.office-outer-box {
    background: grey;
    overflow-y: auto;
    overflow-x: auto;
    padding: 0 10px;
}
</style>
